// services/account-setting.ts
import { useMutation, useQuery, useQueryClient } from 'react-query';
import { api } from '../constants';
import { AxiosInstance } from '../axios.config';
import { IChangePassword, ICountry, IGender } from './types';

export type IAccountDetails = {
  company_address: string;
  created_at: string;
  email: string;
  phone: string;
  role: string;
  shop_name: string;
  telephone: string;
  currency?: string;
  certificate_image?: string;
  pan_vat_certificate_image?: string;
};

const getAccountDetails = async (): Promise<IAccountDetails> => {
  const response = await AxiosInstance.get(api.account.details);
  return response.data;
};

const useGetAccountDetails = () => {
  return useQuery('account-details', getAccountDetails);
};

const updateAccountDetails = async (data: FormData) => {
  const response = await AxiosInstance.patch(api.account.details, data);
  return response.data;
};

const useUpdateAccountDetails = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: updateAccountDetails,
    onSuccess: (data) => {
      // Update the cache manually instead of invalidating
      queryClient.setQueryData('account-details', data);
    },
  });
};

const deleteAccount = async () => {
  const response = await AxiosInstance.delete(api.account.details);
  return response.data;
};

const useDeleteAccount = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: deleteAccount,
    onSuccess: () => {
      queryClient.invalidateQueries('account-details');
    },
  });
};

const changePassword = async (data: IChangePassword) => {
  const response = await AxiosInstance.patch(api.account.changePassword, data);
  return response.data;
};

const useChangePassword = () => {
  return useMutation(changePassword);
};

const getCountries = async (): Promise<ICountry[]> => {
  const response = await AxiosInstance.get(`${api.core.countries}?page_size=300`);
  return response.data;
};

const useGetCountries = () => {
  return useQuery('countries', getCountries, {
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

const getGenders = async (): Promise<IGender[]> => {
  const response = await AxiosInstance.get(api.core.genders);
  return response.data;
};

const useGetGenders = () => {
  return useQuery('genders', getGenders, {
    staleTime: 5 * 60 * 1000, // 5 minutes
  });
};

export {
  useGetAccountDetails,
  useUpdateAccountDetails,
  useDeleteAccount,
  useChangePassword,
  useGetCountries,
  useGetGenders,
};
